import { useCallback, useEffect } from 'react'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useSubscription } from './useWebSocket'
import { useAuthStore } from '@/stores/authStore'
import type { Chat, ChatMessage, ChatMembership, SendMessageParams } from '@/types/chat'

/**
 * Hook for managing chat functionality.
 *
 * Combines React Query for data fetching with WebSocket subscriptions
 * for real-time updates. This mirrors the v1 chat.js functionality.
 */

// Query keys for cache management
export const chatKeys = {
  all: ['chats'] as const,
  lists: () => [...chatKeys.all, 'list'] as const,
  list: () => [...chatKeys.lists()] as const,
  detail: (id: string) => [...chatKeys.all, 'detail', id] as const,
  messages: (chatId: string) => [...chatKeys.all, 'messages', chatId] as const,
  members: (chatId: string) => [...chatKeys.all, 'members', chatId] as const,
}

/**
 * Hook to fetch all user's chats.
 */
export function useChats() {
  const queryClient = useQueryClient()

  // Fetch chats
  const query = useQuery({
    queryKey: chatKeys.list(),
    queryFn: () => api.get<Chat[]>('/api/v2/my/chats'),
  })

  // Subscribe to real-time chat updates
  useSubscription<Chat>('/user/topic/my/chats', (chat) => {
    // Update cache with new/updated chat
    queryClient.setQueryData<Chat[]>(chatKeys.list(), (oldChats) => {
      if (!oldChats) return [chat]

      const existingIndex = oldChats.findIndex((c) => c.id === chat.id)

      if (chat.status === 'DELETED') {
        // Remove deleted chat
        return oldChats.filter((c) => c.id !== chat.id)
      }

      if (existingIndex >= 0) {
        // Update existing chat
        const newChats = [...oldChats]
        newChats[existingIndex] = chat
        return newChats
      }

      // Add new chat at the beginning
      return [chat, ...oldChats]
    })
  })

  return query
}

/**
 * Hook to fetch messages for a specific chat with infinite scroll support.
 *
 * Uses cursor-based pagination with `beforeDate` parameter to load older messages.
 * Messages are fetched in pages (newest first from API), then combined and sorted.
 */
export function useChatMessages(chatId: string | null) {
  const queryClient = useQueryClient()

  const query = useInfiniteQuery({
    queryKey: chatKeys.messages(chatId || ''),
    queryFn: async ({ pageParam }) => {
      const params: Record<string, string> = {}
      if (pageParam) {
        params.beforeDate = pageParam
      }
      const messages = await api.get<ChatMessage[]>(`/api/v2/messages/chat/${chatId}`, params)
      // Sort messages ascending by date (oldest first, newest at bottom)
      return messages.sort(
        (a, b) => new Date(a.dateSent).getTime() - new Date(b.dateSent).getTime()
      )
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage, allPages) => {
      // If we got messages, return the oldest message's date as cursor for next page
      // This loads OLDER messages (scrolling up loads history)

      // Stop if we got an empty page (no more messages)
      if (!lastPage || lastPage.length === 0) {
        return undefined
      }

      // Stop if we got fewer than expected (API typically returns 20 messages)
      // This indicates we've reached the beginning of the chat history
      if (lastPage.length < 20) {
        return undefined
      }

      // Check if we're getting duplicate pages (same oldest date)
      if (allPages.length > 1) {
        const prevPage = allPages[allPages.length - 2]
        if (prevPage && prevPage.length > 0 && prevPage[0].dateSent === lastPage[0].dateSent) {
          return undefined // Same cursor = no new messages
        }
      }

      return lastPage[0].dateSent // oldest message in this page
    },
    enabled: !!chatId,
  })

  // Subscribe to real-time message updates for this chat
  useSubscription<ChatMessage>(chatId ? `/user/topic/chatmessages/${chatId}` : null, (message) => {
    if (message.chatId !== chatId) return

    // Update the infinite query data structure
    queryClient.setQueryData(chatKeys.messages(chatId), (oldData: typeof query.data) => {
      if (!oldData) return oldData

      // Find if message exists in any page
      let messageExists = false
      const newPages = oldData.pages.map((page) => {
        const existingIndex = page.findIndex((m) => m.id === message.id)

        if (message.status === 'DELETED') {
          return page.filter((m) => m.id !== message.id)
        }

        if (existingIndex >= 0) {
          messageExists = true
          const newPage = [...page]
          newPage[existingIndex] = message
          return newPage
        }

        return page
      })

      // If message doesn't exist and isn't deleted, add to first page (newest messages)
      if (!messageExists && message.status !== 'DELETED') {
        const firstPage = [...(newPages[0] || []), message]
        // Sort the first page
        firstPage.sort((a, b) => new Date(a.dateSent).getTime() - new Date(b.dateSent).getTime())
        newPages[0] = firstPage
      }

      return {
        ...oldData,
        pages: newPages,
      }
    })

    // Also update the chat's last message in the chat list
    queryClient.setQueryData<Chat[]>(chatKeys.list(), (oldChats) => {
      if (!oldChats) return oldChats

      return oldChats.map((chat) => {
        if (chat.id === chatId) {
          return {
            ...chat,
            lastMessage: message,
          }
        }
        return chat
      })
    })
  })

  return query
}

/**
 * Hook to send a message.
 */
export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ chatId, content }: SendMessageParams) => {
      console.log('[useSendMessage] Sending message:', { chatId, content: content.substring(0, 50) })
      // Note: Don't use encodeURIComponent here - URLSearchParams handles encoding automatically
      const result = await api.post<ChatMessage>('/api/v2/messages', undefined, {
        chatId,
        content,
      })
      console.log('[useSendMessage] API response:', result)
      return result
    },
    onSuccess: (newMessage, variables) => {
      const chatId = newMessage?.chatId || variables.chatId
      console.log('[useSendMessage] onSuccess:', { newMessage, chatId })

      // If API returns full message, add it to the infinite query cache
      if (newMessage && newMessage.id) {
        queryClient.setQueryData(
          chatKeys.messages(chatId),
          (oldData: { pages: ChatMessage[][]; pageParams: (string | undefined)[] } | undefined) => {
            console.log('[useSendMessage] Updating cache, oldData pages:', oldData?.pages?.length)
            if (!oldData || !oldData.pages || oldData.pages.length === 0) {
              console.log('[useSendMessage] No existing data, creating new')
              return { pages: [[newMessage]], pageParams: [undefined] }
            }

            // Check if message already exists in any page (from WebSocket)
            const messageExists = oldData.pages.some((page) =>
              page.some((m) => m.id === newMessage.id)
            )
            if (messageExists) {
              console.log('[useSendMessage] Message already exists in cache')
              return oldData
            }

            // Add to first page (newest messages) and sort
            const newFirstPage = [...oldData.pages[0], newMessage].sort(
              (a, b) => new Date(a.dateSent).getTime() - new Date(b.dateSent).getTime()
            )
            console.log('[useSendMessage] Added message to cache, new page length:', newFirstPage.length)

            return {
              ...oldData,
              pages: [newFirstPage, ...oldData.pages.slice(1)],
            }
          }
        )
      } else {
        console.log('[useSendMessage] No message ID in response, cannot update cache')
      }
    },
    onError: (error) => {
      console.error('[useSendMessage] Error:', error)
    },
  })
}

/**
 * Hook to mark a chat as read.
 */
export function useMarkChatAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (chatId: string) => api.put<void>('/api/chats/read', undefined, { chatId }),
    onSuccess: (_, chatId) => {
      // Update unread count in cache
      queryClient.setQueryData<Chat[]>(chatKeys.list(), (oldChats) => {
        if (!oldChats) return oldChats

        return oldChats.map((chat) => {
          if (chat.id === chatId) {
            return { ...chat, numOfUnreadMessages: 0 }
          }
          return chat
        })
      })

      // Mark messages as read (update infinite query structure)
      queryClient.setQueryData(
        chatKeys.messages(chatId),
        (oldData: { pages: ChatMessage[][]; pageParams: (string | undefined)[] } | undefined) => {
          if (!oldData || !oldData.pages) return oldData

          const newPages = oldData.pages.map((page) =>
            page.map((message) => ({ ...message, read: true }))
          )

          return { ...oldData, pages: newPages }
        }
      )
    },
  })
}

/**
 * Hook to get unread chat count.
 */
export function useUnreadChatCount() {
  return useQuery({
    queryKey: ['chats', 'unread', 'count'],
    queryFn: () => api.get<number>('/api/v2/my/chats/unread/count'),
    refetchInterval: 30000, // Refetch every 30 seconds
  })
}

/**
 * Hook to create a new chat with a user.
 *
 * Creates the chat first, then adds the target user as a member.
 * Returns the new chat ID for navigation.
 */
export function useCreateChat() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (targetUserId: string): Promise<string> => {
      // Step 1: Create the chat
      const chat = await api.post<Chat>('/api/v2/chats')

      // Step 2: Add the target user as a member
      await api.post('/api/v2/chatmemberships', undefined, {
        userId: targetUserId,
        chatId: chat.id,
      })

      return chat.id
    },
    onSuccess: () => {
      // Invalidate chat list to refetch with new chat
      queryClient.invalidateQueries({ queryKey: chatKeys.list() })
    },
  })
}

/**
 * Combined hook for chat room functionality.
 *
 * Provides:
 * - Flattened messages array from all loaded pages
 * - Infinite scroll functions (fetchNextPage, hasNextPage)
 * - Send message functionality
 * - Auto mark-as-read
 */
export function useChatRoom(chatId: string | null) {
  const { data: chats } = useChats()
  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChatMessages(chatId)
  const sendMessage = useSendMessage()
  const markAsRead = useMarkChatAsRead()
  const { username } = useAuthStore()

  // Get the current chat
  const currentChat = chats?.find((c) => c.id === chatId) || null

  // Flatten all pages into a single sorted array
  // Pages are stored as: [newest page, older page, oldest page, ...]
  // We need to reverse to get: [oldest..., older..., newest...]
  // Each page is already sorted ascending internally
  const messages = messagesData?.pages.slice().reverse().flat() || []

  // Mark as read when chat is opened
  useEffect(() => {
    if (chatId && currentChat?.numOfUnreadMessages) {
      markAsRead.mutate(chatId)
    }
  }, [chatId, currentChat?.numOfUnreadMessages, markAsRead])

  // Send a message
  const send = useCallback(
    async (content: string) => {
      if (!chatId || !content.trim()) return

      await sendMessage.mutateAsync({
        chatId,
        content: content.trim(),
      })
    },
    [chatId, sendMessage]
  )

  // Load older messages (for scroll-to-top infinite scroll)
  const loadOlderMessages = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return {
    chat: currentChat,
    messages,
    isLoading: messagesLoading,
    error: messagesError,
    sendMessage: send,
    isSending: sendMessage.isPending,
    currentUsername: username,
    // Infinite scroll props
    loadOlderMessages,
    hasOlderMessages: hasNextPage,
    isLoadingOlder: isFetchingNextPage,
  }
}

/**
 * Hook to fetch chat members.
 */
export function useChatMembers(chatId: string) {
  return useQuery({
    queryKey: chatKeys.members(chatId),
    queryFn: () => api.get<ChatMembership[]>(`/api/v2/chatmemberships/chat/${chatId}`),
    enabled: !!chatId,
  })
}

/**
 * Hook to add a member to a chat.
 */
export function useAddChatMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ chatId, userId }: { chatId: string; userId: string }) =>
      api.post<ChatMembership>('/api/v2/chatmemberships', undefined, {
        chatId,
        userId,
      }),
    onSuccess: (_, variables) => {
      // Invalidate members list
      queryClient.invalidateQueries({ queryKey: chatKeys.members(variables.chatId) })
      // Also refresh chat list (participant count may change)
      queryClient.invalidateQueries({ queryKey: chatKeys.list() })
    },
  })
}

/**
 * Hook to remove a member from a chat.
 */
export function useRemoveChatMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ membershipId }: { chatId: string; membershipId: string }) =>
      api.delete<void>(`/api/v2/chatmemberships/${membershipId}`),
    onSuccess: (_, variables) => {
      // Invalidate members list
      queryClient.invalidateQueries({ queryKey: chatKeys.members(variables.chatId) })
      // Also refresh chat list (participant count may change)
      queryClient.invalidateQueries({ queryKey: chatKeys.list() })
    },
  })
}
